import React, { useRef, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import FetchModule from '../../Share/Network/FetchModule';
import { useLanguage } from '../../../Context/LanguageContext';
import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

export default function SecondPasswordChangeModal({ onClickExit }) {
  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [prevDigits, setPrevDigits] = useState(['', '', '', '']);
  const [newDigits, setNewDigits] = useState(['', '', '', '']);
  const [confirmDigits, setConfirmDigits] = useState(['', '', '', '']);

  const [isMismatch, setIsMismatch] = useState(false);
  const [isPrevIncorrect, setIsPrevIncorrect] = useState(false);

  const prevRefs = [useRef(), useRef(), useRef(), useRef()];
  const newRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmRefs = [useRef(), useRef(), useRef(), useRef()];

  // 화면 스크롤 제어 //
  useEffect(() => {
    document.body.style = `overflow: hidden`;
    return () => document.body.style = `overflow: auto`;
  }, []);

  // 숫자 입력 처리 //
  function handleChange(row, col, e) {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(-1);

    setIsMismatch(false);
    setIsPrevIncorrect(false);

    if (row === 'prev') {
      const updated = [...prevDigits];
      updated[col] = val;
      setPrevDigits(updated);
      if (val !== '' && col < 3) prevRefs[col + 1].current.focus();
      else if (val !== '' && col === 3) newRefs[0].current.focus();
    }
    else if (row === 'new') {
      const updated = [...newDigits];
      updated[col] = val;
      setNewDigits(updated);
      if (val !== '' && col < 3) newRefs[col + 1].current.focus();
      else if (val !== '' && col === 3) confirmRefs[0].current.focus();
    }
    else {
      const updated = [...confirmDigits];
      updated[col] = val;
      setConfirmDigits(updated);
      if (val !== '' && col < 3) confirmRefs[col + 1].current.focus();
    }
  }

  // 백스페이스 처리 //
  function handleKeyDown(e, row, col) {
    if (e.key !== 'Backspace') return;

    setIsMismatch(false);
    setIsPrevIncorrect(false);

    if (row === 'prev') {
      if (prevDigits[col] !== '') {
        const updated = [...prevDigits];
        updated[col] = '';
        setPrevDigits(updated);
      } else if (col > 0) {
        prevRefs[col - 1].current.focus();
        const updated = [...prevDigits];
        updated[col - 1] = '';
        setPrevDigits(updated);
      }
    }
    else if (row === 'new') {
      if (newDigits[col] !== '') {
        const updated = [...newDigits];
        updated[col] = '';
        setNewDigits(updated);
      } else if (col > 0) {
        newRefs[col - 1].current.focus();
        const updated = [...newDigits];
        updated[col - 1] = '';
        setNewDigits(updated);
      }
    }
    else {
      if (confirmDigits[col] !== '') {
        const updated = [...confirmDigits];
        updated[col] = '';
        setConfirmDigits(updated);
      } else if (col > 0) {
        confirmRefs[col - 1].current.focus();
        const updated = [...confirmDigits];
        updated[col - 1] = '';
        setConfirmDigits(updated);
      }
    }
  }

  // 변경하기 클릭 //
  function onClickSave() {
    const prevPw = prevDigits.join('');
    const newPw = newDigits.join('');
    const confirmPw = confirmDigits.join('');

    if (prevPw.length < 4) {
      alert(t('myStoreSetting.account.secondPasswordChange.alertPrevIncomplete'));
      return;
    }
    if (newPw.length < 4) {
      alert(t('myStoreSetting.account.secondPasswordChange.alertNewIncomplete'));
      return;
    }
    if (confirmPw.length < 4) {
      alert(t('myStoreSetting.account.secondPasswordChange.alertConfirmIncomplete'));
      return;
    }
    if (newPw !== confirmPw) {
      setIsMismatch(true);
      return;
    }

    const requestData = {
      prevSecondPw: prevPw,
      newSecondPw: newPw
    };

    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postData('second_auth/change', 'POST', requestData);

      if (responseData.status === 200) {
        alert(t('myStoreSetting.account.secondPasswordChange.alertSuccess'));
        onClickExit();
      }
      else if (responseData.status === 201) {
        setIsPrevIncorrect(true);
        setPrevDigits(['', '', '', '']);
        prevRefs[0].current.focus();
      }
    })();
  }

  return (
    <TotalModalWrapper>
      <Modal>
        <TitleRow>
          <TitleGroup>
            <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} hover='default'>{t('myStoreSetting.account.secondPasswordChange.title')}</Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} hover='default'>{t('myStoreSetting.account.secondPasswordChange.initialHint')}</Text>
          </TitleGroup>
          <CancelButton onClick={onClickExit}>
            {t('myStoreSetting.account.secondPasswordChange.cancel')}
          </CancelButton>
        </TitleRow>
        <SectionWrapper>
          <Section>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('myStoreSetting.account.secondPasswordChange.currentPassword')}</Text>
            <DigitRow>
              {prevDigits.map((digit, i) => (
                <DigitBox key={i} isError={isPrevIncorrect} onClick={() => prevRefs[i].current.focus()}>
                  <DigitInput
                    ref={prevRefs[i]}
                    value={digit}
                    onChange={(e) => handleChange('prev', i, e)}
                    onKeyDown={(e) => handleKeyDown(e, 'prev', i)}
                  />
                </DigitBox>
              ))}
            </DigitRow>
          </Section>
          <Section>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('myStoreSetting.account.secondPasswordChange.newPassword')}</Text>
            <DigitRow>
              {newDigits.map((digit, i) => (
                <DigitBox key={i} isError={isMismatch} onClick={() => newRefs[i].current.focus()}>
                  <DigitInput
                    ref={newRefs[i]}
                    value={digit}
                    onChange={(e) => handleChange('new', i, e)}
                    onKeyDown={(e) => handleKeyDown(e, 'new', i)}
                  />
                </DigitBox>
              ))}
            </DigitRow>
          </Section>
          <Section>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('myStoreSetting.account.secondPasswordChange.confirmPassword')}</Text>
            <DigitRow>
              {confirmDigits.map((digit, i) => (
                <DigitBox key={i} isError={isMismatch} onClick={() => confirmRefs[i].current.focus()}>
                  <DigitInput
                    ref={confirmRefs[i]}
                    value={digit}
                    onChange={(e) => handleChange('confirm', i, e)}
                    onKeyDown={(e) => handleKeyDown(e, 'confirm', i)}
                  />
                </DigitBox>
              ))}
            </DigitRow>
          </Section>
        </SectionWrapper>
        <SaveButton isError={isMismatch || isPrevIncorrect} onClick={onClickSave}>
          {(isMismatch || isPrevIncorrect)
            ? (isPrevIncorrect
                ? t('myStoreSetting.account.secondPasswordChange.alertPrevIncorrect')
                : t('myStoreSetting.account.secondPasswordChange.mismatch'))
            : t('myStoreSetting.account.secondPasswordChange.save')}
        </SaveButton>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  padding: 20px;
  gap: 10px;

  width: 285px;

  background: ${props => props.theme.subBackground};
  border-radius: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 4px;
`;


const CancelButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 10px 18px;

  background: ${props => props.theme.buttonFill};
  border-radius: 10px;
  border: 1px solid ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subText};

  @media (hover: hover){
    &:hover {
      cursor: pointer;
      color: ${props => props.theme.text};
      border: 1px solid ${props => props.theme.line};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 5px;
`;

const DigitRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const DigitBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 60px;
  height: 60px;

  border: 2px solid ${props => props.theme.primary};
  border-radius: 10px;

  background-color: ${props => props.theme.subBackground};

  overflow: hidden;

  &:hover {
    cursor: text;
  }

  transition: border-color 0.3s ease-in-out;
`;

const DigitInput = styled.input`
  font-family: NotoSansKR-Medium;
  font-size: 20px;

  padding: 0;

  width: 100%;
  height: 100%;

  text-align: center;

  outline: none;
  background-color: transparent;
  color: ${props => props.theme.primary};
  border: 0;

  &:focus {
    outline: none;
  }
`;




const SaveButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: 35px;

  width: 100%;
  height: 40px;

  background: ${props => props.theme.primaryGradient};
  border-radius: 7px;

  font-family: NotoSansKR-Bold;
  font-size: 16px;
  line-height: 19px;

  color: #FFFFFF;

  &:hover {
    cursor: pointer;
    opacity: 0.9;
  }

  transition: all 0.3s ease-in-out;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  color: ${props => props.color};

  &:hover {
    cursor: ${props => props.hover};
  }

  transition: all 0.3s ease-in-out;
`;